import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  NdcColumnState,
  NdcComponentInputType,
  NdcComponentSequenceType,
  NdcComponentType,
  NdcSpreadsheetInterpretation,
  NdcStartingType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import strUtil from 'util/strUtil';

/** Returns the priority of a component type to be used in displaying options and selecting the default
 * type when there is more than one inferred type, where 0 is the highest priority.
 *
 * Returned priority (0 = highest priority):
 * 0: Existing entity name / Registry ID
 * 1: DNA bases
 * 2: AA sequence
 * */
export const getPriorityForComponentType = (componentType: NdcComponentType): number => {
  if (componentType.componentInputType === NdcComponentInputType.EXISTING_ENTITY_NAME_OR_REGISTRY_ID) {
    return 0;
  } else if (componentType.componentSequenceType === NdcComponentSequenceType.DNA) {
    return 1;
  } else if (componentType.componentSequenceType === NdcComponentSequenceType.AA) {
    return 2;
  }
  assert(
    false,
    `Invalid component type ${componentType.componentSequenceType} ${componentType.componentInputType}`
  );
};

/** Resolves the effective type for a column, using the selectedType if available, falling back to the
 * highest-priority inferred type. See the docstring on NdcColumnState.selectedType for more details on when
 * selectedType is set. */
export function getResolvedColumnType(column: NdcColumnState): NdcComponentType {
  // selectedType can only be set if inferredTypes is non-null and non-empty
  assert(
    column.inferredTypes != null && column.inferredTypes.length > 0,
    'Column should have inferred types'
  );

  return (
    column.selectedType ??
    column.inferredTypes.sort((a, b) => getPriorityForComponentType(a) - getPriorityForComponentType(b))[0]
  );
}

/** Returns true if any column's resolved type is a DNA sequence type.
 *
 * Will assert that the columns' inferredTypes is non-null and non-empty. */
export function columnsIncludeDnaType(columns: Array<NdcColumnState>): boolean {
  return columns.some(
    (column) => getResolvedColumnType(column).componentSequenceType === NdcComponentSequenceType.DNA
  );
}

/** Returns an error message for additional columns in the spreadsheet. */
export function getErrorMessageForAdditionalColumns(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation,
  startingType: NdcStartingType
): string | null {
  const additionalColumns = spreadsheetInterpretation.columns
    .filter((column) => column.errorMessage != null && /is not a valid .+ name$/.test(column.errorMessage))
    .map((column) => column.columnName);

  if (additionalColumns.length === 0) {
    return null;
  }

  return `Your file has ${additionalColumns.length} ${pluralize(additionalColumns.length, 'additional column')}: \
${strUtil.getReadableStringFromList(additionalColumns)}. Please remove \
${pluralize(additionalColumns.length, 'this column', 'these columns')} or ensure your headers match the \
required ${startingType.toLowerCase().slice(0, -1)} columns, then re-upload your spreadsheet.`;
}

export interface CountsOfCreatedComponents {
  dnaDomains: number;
  aaDomains: number;
  dnaChains: number;
  aaChains: number;
  dnaPairs: number;
  aaPairs: number;
  antibodies: number;
  totalEntities: number;
}

/** Calculates the expected counts of entities that will be created during NDC spreadsheet import.
 *
 * TODO(BNCH-143417): Implement this util
 * TODO(BNCH-143416): Calculate the number of DNA/AA Pairs that will be created
 */
export function getCountsOfCreatedComponents(
  spreadsheetInterpretation: NdcSpreadsheetInterpretation,
  selectedStartingType: NdcStartingType,
  antibodyFormat: AntibodyFormat
): CountsOfCreatedComponents {
  const rowCount = spreadsheetInterpretation.rowCount;
  const numDistinctDomains = antibodyFormat.distinctDomains.length;
  const numDistinctChains = antibodyFormat.distinctChains.length;
  const hasDnaColumns = columnsIncludeDnaType(spreadsheetInterpretation.columns);

  const counts: CountsOfCreatedComponents = {
    dnaDomains: hasDnaColumns ? rowCount * numDistinctDomains : 0,
    aaDomains: rowCount * numDistinctDomains,
    dnaChains: hasDnaColumns ? rowCount * numDistinctChains : 0,
    aaChains: rowCount * numDistinctChains,
    dnaPairs: hasDnaColumns ? rowCount : 0, // Assuming 1 single pair per format for now
    aaPairs: rowCount, // Assuming 1 single pair per format for now
    antibodies: rowCount,
    totalEntities: 0,
  };

  counts.totalEntities =
    counts.dnaDomains +
    counts.aaDomains +
    counts.dnaChains +
    counts.aaChains +
    counts.dnaPairs +
    counts.aaPairs +
    counts.antibodies;

  return counts;
}
